import { useEffect, useRef, useState } from "react";
import { State } from "redux/store";
import { connect, useDispatch } from "react-redux";
import {
  AnnotationSources,
  ContactUsComponentType,
  StepTileProgressStatus,
} from "types";

import StepTile from "../StepTile";
import ChevronIcon from "assets/icons/ChevronIcon";
import AnnotationsIcon from "assets/icons/AnnotationIcon";

import {
  getAnnotationStatus,
  getIsDatasetIndexing,
  getNewDatasetId,
  getIsNewDatasetInitializing,
  getLastUsedData,
  getIsNewDatasetReady,
  getUploadStatus,
} from "redux/CreateDataset/selectors";
import { renderHeadIcon } from "../helpers";
import { skipAnnotations } from "redux/CreateDataset/actions";
import { setShowDemoRequestModal } from "redux/Modals/actions";

import AnnotationsDropdown from "./AnnotationsDropdown";
import PulsingDropdownIcon from "../PulsingDropdownIcon";
import AnnotationsInputBanner from "./AnnotationsInputBanner";

import styles from "./style.module.scss";

const AnnotationStep = ({
  annotationStatus,
  datasetIndexing,
  newDatasetId,
  isNewDatasetInitializing,
  lastUsedAnnotationFiles,
  isDatasetReady,
  uploadStatus,
}: AnnotationStepProps) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDropdown, setShowdropdown] = useState(false);
  const [showCVATSelector, setShowCVATSelector] = useState(false);
  const dispatch = useDispatch();

  const bannerRef = useRef<HTMLDivElement>(null);
  const editButtonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const cvatSelectorRef = useRef<HTMLDivElement>(null);

  const annotationComplete =
    annotationStatus === StepTileProgressStatus.COMPLETE;
  const annotationActive = annotationStatus === StepTileProgressStatus.ACTIVE;
  const isDataUploading = uploadStatus === StepTileProgressStatus.IN_PROGRESS;

  const toggleDropdown = () => {
    setShowdropdown(!showDropdown);
    if (showDropdown) {
      setShowBanner(false);
      setShowCVATSelector(false);
    }
  };

  const handleOutsideClick = (event: MouseEvent) => {
    const editButton = editButtonRef.current;
    const banner = bannerRef.current;
    const dropdown = dropdownRef.current;
    const cvatSelector = cvatSelectorRef.current;

    const editButtonClicked =
      editButton && editButton.contains(event.target as Node);
    const bannerClicked = banner && banner.contains(event.target as Node);
    const dropdownClicked = dropdown && dropdown.contains(event.target as Node);
    const cvatSelectorClicked =
      cvatSelector && cvatSelector.contains(event.target as Node);

    if (showBanner && showDropdown) {
      if (!editButtonClicked && !bannerClicked && !dropdownClicked) {
        setShowBanner(false);
        setShowdropdown(false);
      }
    } else if (showDropdown) {
      if (!editButtonClicked && !dropdownClicked) {
        setShowdropdown(false);
        setShowCVATSelector(false);
      }
    } else if (showBanner) {
      if (!editButtonClicked && !bannerClicked) {
        setShowBanner(false);
      }
    } else if (showCVATSelector) {
      if (!editButtonClicked && !cvatSelectorClicked && !dropdownClicked) {
        setShowCVATSelector(false);
      }
    }
  };

  const handleEditClick = () => {
    setShowBanner(false);
    setShowCVATSelector(false);
    setShowdropdown(!showDropdown);
  };

  const closeBanner = () => {
    setShowBanner(false);
  };

  const closeOnBannerActionClick = () => {
    setShowBanner(false);
    setShowdropdown(false);
  };

  const onSkipClick = () => {
    closeOnBannerActionClick();
    dispatch(skipAnnotations(newDatasetId) as any);
  };

  const renderActionIcon = () => {
    if (datasetIndexing || isDatasetReady || isDataUploading) {
      return <noscript />;
    }

    if (annotationComplete) {
      return (
        <div
          ref={editButtonRef}
          className={styles.actionButton}
          onClick={handleEditClick}
        >
          Edit
        </div>
      );
    }
    if (showDropdown) {
      return (
        <div
          ref={editButtonRef}
          onClick={handleEditClick}
          className={styles.openIcon}
        >
          <ChevronIcon direction="up" />
        </div>
      );
    } else if (annotationActive && !showDropdown) {
      return (
        <PulsingDropdownIcon
          showPulse={!showBanner}
          isDropdownOpen={showBanner}
          toggleDropdown={toggleDropdown}
          containerRef={editButtonRef}
        />
      );
    }
  };

  const handleContactUsClick = (calledFrom: ContactUsComponentType) => {
    dispatch(setShowDemoRequestModal({ flag: true, calledFrom }));
    setShowdropdown(false);
  };

  const handleCVATImportClick = () => {
    setShowdropdown(false);
    closeBanner();
  };

  const handleAnnotationOptionSelection = (
    annotationSources: AnnotationSources
  ) => {
    if (
      isNewDatasetInitializing &&
      annotationSources === AnnotationSources.UPLOAD
    ) {
      setShowBanner(true);
      setShowCVATSelector(false);
    } else if (
      isNewDatasetInitializing &&
      annotationSources === AnnotationSources.CVAT
    ) {
      setShowCVATSelector(true);
      setShowBanner(false);
    } else if (
      isNewDatasetInitializing &&
      annotationSources === AnnotationSources.LABELBOX
    ) {
      setShowBanner(false);
      setShowCVATSelector(false);
      // labelBox selection
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [showBanner, showDropdown]);

  return (
    <div className={styles.annotationsContainer}>
      <StepTile
        title={"Add Annotations"}
        status={annotationStatus}
        headIcon={renderHeadIcon(<AnnotationsIcon />, annotationStatus)}
        actionIcon={renderActionIcon()}
        handleOutsideClick={handleOutsideClick}
        isContentShown={showBanner}
      />

      <AnnotationsInputBanner
        newDatasetId={newDatasetId}
        showBanner={showBanner}
        closeBanner={closeBanner}
        closeOnBannerActionClick={closeOnBannerActionClick}
        bannerRef={bannerRef}
        viewOnly={!isNewDatasetInitializing}
        lastUsedAnnotationFiles={lastUsedAnnotationFiles}
      />

      {showDropdown && (
        <AnnotationsDropdown
          onSkipClick={onSkipClick}
          dropdownRef={dropdownRef}
          cvatSelectorRef={cvatSelectorRef}
          showCVATSelector={showCVATSelector}
          handleContactUsClick={handleContactUsClick}
          handleCVATImportClick={handleCVATImportClick}
          handleSelection={handleAnnotationOptionSelection}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    annotationStatus: getAnnotationStatus(state),
    uploadStatus: getUploadStatus(state),
    newDatasetId: getNewDatasetId(state),
    isDatasetReady: getIsNewDatasetReady(state),
    datasetIndexing: getIsDatasetIndexing(state),
    isNewDatasetInitializing: getIsNewDatasetInitializing(state),
    lastUsedAnnotationFiles: getLastUsedData(state).annotationFiles,
  };
};

export default connect(mapStateToProps)(AnnotationStep);

interface AnnotationStepProps {
  annotationStatus: StepTileProgressStatus;
  uploadStatus: StepTileProgressStatus;
  newDatasetId: string;
  datasetIndexing: boolean;
  isDatasetReady: boolean;
  isNewDatasetInitializing: boolean;
  lastUsedAnnotationFiles: string[];
}
